import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ProductCard from '../components/ProductCard';
import { products } from '../data/products';

const Shop = () => {
  const [filter, setFilter] = useState('All');
  const [difficultyFilter, setDifficultyFilter] = useState('All');

  const categories = ['All', 'Plants', 'Bundles', 'Accessories'];
  const difficulties = ['All', 'Easy', 'Medium', 'Hard'];

  const filteredProducts = products.filter(product => {
    const matchCategory = filter === 'All' || 
      (filter === 'Plants' && product.category === 'plant') ||
      (filter === 'Bundles' && product.category === 'bundle') ||
      (filter === 'Accessories' && product.category === 'accessory');
    
    const matchDifficulty = difficultyFilter === 'All' || product.difficulty === difficultyFilter;

    return matchCategory && matchDifficulty;
  });

  return (
    <div className="min-h-screen bg-cream py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-serif text-botanical-dark mb-4">Shop Collections</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">Discover our curated selection of healthy plants, premium pots, and beginner-friendly bundles.</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters */}
          <div className="w-full lg:w-64 shrink-0">
            <div className="bg-white p-6 rounded-2xl shadow-sm sticky top-28">
              <div className="mb-8">
                <h3 className="font-serif text-lg text-botanical-dark mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiIcons.FiFilter} className="text-clay" /> Categories
                </h3>
                <ul className="space-y-3">
                  {categories.map(cat => (
                    <li key={cat}>
                      <button 
                        onClick={() => setFilter(cat)}
                        className={`text-sm transition-colors ${filter === cat ? 'text-clay font-medium' : 'text-gray-600 hover:text-botanical'}`}
                      >
                        {cat}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="font-serif text-lg text-botanical-dark mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiIcons.FiActivity} className="text-clay" /> Care Level
                </h3>
                <ul className="space-y-3">
                  {difficulties.map(diff => (
                    <li key={diff}>
                      <button 
                        onClick={() => setDifficultyFilter(diff)}
                        className={`text-sm transition-colors ${difficultyFilter === diff ? 'text-clay font-medium' : 'text-gray-600 hover:text-botanical'}`}
                      >
                        {diff}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Product Grid */}
          <div className="flex-1">
            <div className="mb-6 flex justify-between items-center text-sm text-gray-500">
              <span>Showing {filteredProducts.length} results</span>
              <select className="bg-transparent border-none focus:ring-0 cursor-pointer text-botanical-dark font-medium">
                <option>Featured</option>
                <option>Price: Low to High</option>
                <option>Price: High to Low</option>
              </select>
            </div>

            <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              <AnimatePresence>
                {filteredProducts.map(product => (
                  <motion.div
                    key={product.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ProductCard product={product} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
            
            {filteredProducts.length === 0 && (
              <div className="text-center py-20">
                <SafeIcon icon={FiIcons.FiFeather} className="text-4xl text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-serif text-gray-500">No plants match your current filters.</h3>
                <button onClick={() => {setFilter('All'); setDifficultyFilter('All');}} className="mt-4 text-clay hover:underline">Clear all filters</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shop;